import { useCallback, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import useLocalStorageState from 'use-local-storage-state';

import { fetchClient } from 'services/api';
import { userQueryKeys } from './users';
import { useSearchParams } from 'react-router-dom';

const apiClient = fetchClient();

export const useJwt = () => useLocalStorageState('jwt', { defaultValue: '' });

export const useDelegateTokenLS = () => useLocalStorageState('delegateToken', { defaultValue: '' });

export const useIsAuthenticated = () =>
  useLocalStorageState('isAuthenticated', { defaultValue: false });

export const useDelegateToken = () => {
  const [delegateTokenLS, setDelegateTokenLS] = useDelegateTokenLS();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('delegateToken')) {
      setDelegateTokenLS(searchParams.get('delegateToken'));
      searchParams.delete('delegateToken');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setDelegateTokenLS, setSearchParams]);

  const setDelegateToken = useCallback(
    dt => {
      setDelegateTokenLS(dt);
      if (
        !!dt !== !!searchParams.get('delegateToken') ||
        (dt && searchParams.get('delegateToken') && dt !== searchParams.get('delegateToken'))
      ) {
        if (dt) searchParams.set('delegateToken', dt);
        else searchParams.delete('delegateToken');
        setSearchParams(searchParams, { replace: true });
      }
    },
    [setDelegateTokenLS, searchParams, setSearchParams],
  );

  return [searchParams.get('delegateToken') || delegateTokenLS, setDelegateToken];
};

export const useIsDelegated = () => {
  const [delegateToken] = useDelegateToken();
  return !!delegateToken;
};

export const useSignOut = () => {
  const [, setJwt] = useJwt();
  const [, setDelegateToken] = useDelegateTokenLS();
  const queryClient = useQueryClient();

  return useCallback(async () => {
    setJwt('');
    setDelegateToken('');
    return queryClient.resetQueries().then(() => {});
  }, [setJwt, setDelegateToken, queryClient]);
};

const getToken = async (params = {}) => {
  const client = apiClient;
  const url = '/auth/create-token';
  const { data: response } = await client.post(url, params);
  return response;
};

export const useGetToken = () => {
  const queryClient = useQueryClient();
  return useMutation(({ email, invite, context }) => getToken({ email, invite, context }), {
    onSuccess: () =>
      queryClient.getQueryData(userQueryKeys.me) &&
      queryClient.setQueryData(oldMe => ({ ...oldMe, hasEmail: true })),
  });
};

async function verifyToken(params) {
  return await apiClient.post('/auth/verify-token', params);
}

export const useVerifyToken = () => {
  const queryClient = useQueryClient();
  const [, setDelegateToken] = useDelegateTokenLS();

  return useMutation(({ token }) => verifyToken({ token: token }).then(res => res.data), {
    onSuccess: data => {
      setDelegateToken('');

      queryClient.setQueryData(userQueryKeys.me, oldMe => {
        return { ...oldMe, data: data.data };
      });
    },
  });
};
